'use client'

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import {
  breakpoints,
  VIEWPORT,
} from '@pearlconvert/ui/providers/breakpoint/constants'
import {
  Breakpoint,
  BreakpointProviderValueProps,
  Viewport,
} from '@pearlconvert/ui/providers/breakpoint/types'

const initialValue: BreakpointProviderValueProps = {
  isBreakpointEqual: () => false,
  isBreakpointUp: () => false,
  isBreakpointDown: () => false,
  isMobileView: () => false,
  isDesktopView: () => false,
}

export const BreakpointContext =
  createContext<BreakpointProviderValueProps>(initialValue)
const { Provider } = BreakpointContext

const getCurrentBreakpoint = () => {
  return getComputedStyle(document.documentElement).getPropertyValue(
    '--global__breakpoint'
  ) as Breakpoint
}

const getCurrentViewport = () => {
  return getComputedStyle(document?.documentElement)?.getPropertyValue(
    '--global__viewport'
  ) as Viewport
}

export const BreakpointProvider = ({ children }: { children: ReactNode }) => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint | undefined>(
    undefined
  )
  const [viewport, setViewport] = useState<Viewport | undefined>(undefined)

  const isBreakpointEqual = useCallback(
    (query: Breakpoint) => {
      return breakpoint === query
    },
    [breakpoint]
  )
  const isBreakpointUp = useCallback(
    (query: Breakpoint) => {
      return (
        breakpoints.indexOf(query) <=
        breakpoints.indexOf(breakpoint as Breakpoint)
      )
    },
    [breakpoint]
  )
  const isBreakpointDown = useCallback(
    (query: Breakpoint) => {
      return (
        breakpoints.indexOf(query) >
        breakpoints.indexOf(breakpoint as Breakpoint)
      )
    },
    [breakpoint]
  )

  const isMobileView = useCallback(() => {
    return viewport === VIEWPORT.MOBILE
  }, [viewport])

  const isDesktopView = useCallback(() => {
    return viewport === VIEWPORT.DESKTOP
  }, [viewport])

  useLayoutEffect(() => {
    const breakpointListener = () => {
      setViewport(getCurrentViewport())
      setBreakpoint(getCurrentBreakpoint())
    }

    breakpointListener()

    window.addEventListener('resize', breakpointListener)
    return () => window.removeEventListener('resize', breakpointListener)
  }, [])

  const value = useMemo(() => {
    return {
      isBreakpointEqual,
      isBreakpointUp,
      isBreakpointDown,
      isMobileView,
      isDesktopView,
    }
  }, [
    isBreakpointEqual,
    isBreakpointUp,
    isBreakpointDown,
    isMobileView,
    isDesktopView,
  ])

  return <Provider value={value}>{children}</Provider>
}

export const useBreakpoint = () => useContext(BreakpointContext)

export const MobileView = ({ children }: { children: ReactNode }) => {
  const { isMobileView } = useBreakpoint()
  return isMobileView() ? children : null
}

export const DesktopView = ({ children }: { children: ReactNode }) => {
  const { isDesktopView } = useBreakpoint()
  return isDesktopView() ? children : null
}
